import {
  configure,
  hitsPerPage,
  index,
} from "instantsearch.js/es/widgets/index.js";
import algoliasearch from "algoliasearch/lite.js";
import { createNewSearchInstance } from "./components/search-instance.js";
import { filterAssignments } from "./facets.js";
import { getCategorySlug, normalizeCategoryLookupKey } from "./category-slug.js";
import {
  chamfrColumnConfigMiddleware,
  chamfrOutOfStockMiddleware,
  chamfrLoadingSkeletonMiddleware,
} from "./middleware/index.js";
import {
  chamfrClearAllFilters,
  chamfrPagination,
  chamfrProductsHits,
  chamfrProductsSortBy,
  chamfrResultsHeader,
  chamfrSearchBox,
  chamfrSelectedFilters,
} from "./widgets/index.js";
import {
  createCustomLabels,
  createTableColumns,
  renderFilters,
} from "./components/index.js";

const HASH_CATEGORY_PARAM = "category";
const HASH_SUBCATEGORY_PARAM = "subcategory";
const COMPLETE_ITEM_NUMBER_QUERY_PATTERN = /^(?=.{6,}$)(?=.*\d{5})[a-z0-9-]+$/i;

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeFilterValue = (value) => String(value || "").replace(/["\\]/g, "\\$&");

const appendFilter = (currentFilters, nextFilter) =>
  currentFilters ? `(${currentFilters}) AND ${nextFilter}` : nextFilter;

const buildExactItemNumberFilter = (query) => {
  const itemNumbers = [
    ...new Set([
      query,
      query.toUpperCase(),
      query.toLowerCase(),
    ]),
  ];

  const filters = itemNumbers.map(
    (itemNumber) => `item_number:"${escapeFilterValue(itemNumber)}"`,
  );

  return filters.length > 1 ? `(${filters.join(" OR ")})` : filters[0];
};

const isCompleteItemNumberQuery = (query) =>
  COMPLETE_ITEM_NUMBER_QUERY_PATTERN.test(String(query || "").trim());

const createSupplierStoreSearchClient = (searchClient) => ({
  ...searchClient,
  search(requests) {
    return searchClient.search(
      requests.map((request) => {
        const query = String(request.params?.query || "").trim();

        if (
          !isCompleteItemNumberQuery(query) ||
          !String(request.indexName || "").startsWith(algolia.product_index_name)
        ) {
          return request;
        }

        return {
          ...request,
          params: {
            ...request.params,
            filters: appendFilter(
              request.params?.filters,
              buildExactItemNumberFilter(query),
            ),
            restrictSearchableAttributes: ["item_number"],
          },
        };
      }),
    );
  },
});

const buildFilter = ({
  supplierName,
  categoryName,
  subcategoryName,
}) => {
  const filters = [`seller:"${escapeFilterValue(supplierName)}"`];

  if (categoryName) {
    filters.push(`main_category:"${escapeFilterValue(categoryName)}"`);
  }

  if (subcategoryName) {
    filters.push(`category:"${escapeFilterValue(subcategoryName)}"`);
  }

  return filters.join(" AND ");
};

const getHashState = () => {
  const hash = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);

  return {
    category: params.get(HASH_CATEGORY_PARAM),
    subcategory: params.get(HASH_SUBCATEGORY_PARAM),
  };
};

const clearProductsRouteState = (params) => {
  [...params.keys()].forEach((key) => {
    if (key === "products" || key.startsWith("products[")) {
      params.delete(key);
    }
  });
};

const setHashState = ({ category, subcategory, resetProductsRouteState = false }) => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  if (category) {
    hashParams.set(HASH_CATEGORY_PARAM, category);
  } else {
    hashParams.delete(HASH_CATEGORY_PARAM);
  }

  if (subcategory) {
    hashParams.set(HASH_SUBCATEGORY_PARAM, subcategory);
  } else {
    hashParams.delete(HASH_SUBCATEGORY_PARAM);
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (resetProductsRouteState) {
    clearProductsRouteState(searchParams);
  }

  const nextSearch = searchParams.toString();
  const nextHash = hashParams.toString();
  const nextUrl = `${window.location.pathname}${
    nextSearch ? `?${nextSearch}` : ""
  }${
    nextHash ? `#${nextHash}` : ""
  }`;

  window.history.replaceState(null, "", nextUrl);
};

const findCategoryBySlug = (categories, slug) =>
  categories.find((category) => category.slug === slug);

const clearSearchContainers = (supplierStoreElement) => {
  [
    ".search-results-leftside",
    ".selected-filters ul",
    ".selected-filters .clear-all-filters",
    "table.products .header",
    "table.products .list",
    ".chamfr-pagination",
    ".display-per-page span",
  ].forEach((selector) => {
    const element = supplierStoreElement.querySelector(selector);

    if (element) {
      element.innerHTML = "";
    }
  });
};

const getCategorySetup = (category) =>
  category
    ? filterAssignments[category.slug] ?? filterAssignments["DEFAULT"]
    : filterAssignments["SELLER"];

const createCategoryBrowser = (supplierStoreElement) => {
  const countDropdown = supplierStoreElement.querySelector(".count-dropdown");
  const browser = document.createElement("section");
  browser.className = "supplier-category-browser";
  browser.hidden = true;

  if (countDropdown) {
    countDropdown.insertAdjacentElement("beforebegin", browser);
  } else {
    supplierStoreElement.prepend(browser);
  }

  return browser;
};

const renderCountBadge = (count) =>
  `<span class="supplier-category-browser__count">${Number(count || 0).toLocaleString()}</span>`;

const scrollItemIntoHorizontalView = (container, item) => {
  if (!container || !item) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const overflowLeft = itemRect.left - containerRect.left;
  const overflowRight = itemRect.right - containerRect.right;

  if (overflowLeft < 0) {
    container.scrollLeft += overflowLeft;
  } else if (overflowRight > 0) {
    container.scrollLeft += overflowRight;
  }
};

const scrollActiveCategoryBrowserItemsIntoView = (browser) => {
  const activeTab = browser.querySelector(".supplier-category-browser__tab.is-active");
  const activeSubchip = browser.querySelector(".supplier-category-browser__subchip.is-active");

  scrollItemIntoHorizontalView(
    browser.querySelector(".supplier-category-browser__tabs"),
    activeTab,
  );
  scrollItemIntoHorizontalView(
    browser.querySelector(".supplier-category-browser__subchips"),
    activeSubchip,
  );
};

const renderCategoryBrowser = ({
  browser,
  categories,
  activeCategory,
  subcategories,
  selectedSubcategory,
}) => {
  if (!browser || categories.length <= 1) {
    if (browser) {
      browser.hidden = true;
      browser.innerHTML = "";
    }

    return;
  }

  const subcategoryMarkup =
    subcategories.length > 1
      ? `<div class="supplier-category-browser__subcategories">
          <span class="supplier-category-browser__subheading">${escapeHtml(activeCategory.name)}:</span>
          <div class="supplier-category-browser__subchips">
            ${subcategories
              .map((subcategory) => {
                const isActive = selectedSubcategory?.name === subcategory.name;

                return `<button
                  type="button"
                  class="supplier-category-browser__subchip${isActive ? " is-active" : ""}"
                  data-supplier-subcategory="${subcategory.slug}"
                  aria-pressed="${isActive ? "true" : "false"}"
                >
                  <span>${escapeHtml(subcategory.name)}</span>
                  ${renderCountBadge(subcategory.count)}
                </button>`;
              })
              .join("")}
          </div>
        </div>`
      : "";

  browser.hidden = false;
  browser.innerHTML = `
    <h3 class="supplier-category-browser__label">Browse by Category</h3>
    <div class="supplier-category-browser__tabs" role="tablist" aria-label="Supplier product categories">
      ${categories
        .map((category) => {
          const isActive = activeCategory.name === category.name;

          return `<button
            type="button"
            class="supplier-category-browser__tab${isActive ? " is-active" : ""}"
            data-supplier-category="${category.slug}"
            role="tab"
            aria-selected="${isActive ? "true" : "false"}"
          >
            <span>${escapeHtml(category.name)}</span>
            ${renderCountBadge(category.count)}
          </button>`;
        })
        .join("")}
    </div>
    ${subcategoryMarkup}
  `;

  scrollActiveCategoryBrowserItemsIntoView(browser);
};

const fetchCategoryCounts = async ({ client, supplierName }) => {
  const response = await client.search([
    {
      indexName: algolia.product_index_name,
      query: "",
      params: {
        hitsPerPage: 0,
        filters: buildFilter({
          supplierName,
        }),
        facets: ["main_category"],
        maxValuesPerFacet: 100,
      },
    },
  ]);

  return Object.entries(response?.results?.[0]?.facets?.main_category || {})
    .filter(([name, count]) => name && Number(count) > 0)
    .map(([name, count], index) => ({
      name,
      count: Number(count),
      index,
      slug: getCategorySlug(name),
    }))
    .sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }

      return a.index - b.index;
    });
};

const fetchSubcategories = async ({ client, supplierName, category }) => {
  const response = await client.search([
    {
      indexName: algolia.product_index_name,
      query: "",
      params: {
        hitsPerPage: 0,
        filters: buildFilter({
          supplierName,
          categoryName: category.name,
        }),
        facets: ["category"],
        maxValuesPerFacet: 100,
      },
    },
  ]);

  const activeCategoryKey = normalizeCategoryLookupKey(category.name);

  return Object.entries(response?.results?.[0]?.facets?.category || {})
    .filter(
      ([name, count]) =>
        name &&
        Number(count) > 0 &&
        normalizeCategoryLookupKey(name) !== activeCategoryKey,
    )
    .map(([name, count], index) => ({
      name,
      count: Number(count),
      index,
      slug: getCategorySlug(name),
    }))
    .sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }

      return a.index - b.index;
    });
};

const mountCategorySearch = ({
  supplierStoreElement,
  supplierName,
  category,
  getSelectedSubcategory,
  searchClient,
}) => {
  const categorySetup = getCategorySetup(category);
  const sharedColumns = category ? filterAssignments["EVERY"].displayHits : [];
  const sharedFilters = category
    ? filterAssignments["EVERY"].displayFacets
    : [{ key: "availability", orderFacets: 12, adminOnly: false }];

  const columns = [
    ...(categorySetup.displayHits || []),
    ...sharedColumns,
  ].sort((a, b) => a.orderColumns - b.orderColumns);

  const filters = [
    ...(categorySetup.displayFacets || []),
    ...sharedFilters,
  ].sort((a, b) => a.orderFacets - b.orderFacets);

  const search = createNewSearchInstance({ searchClient });
  search.use(chamfrOutOfStockMiddleware);
  search.use(chamfrLoadingSkeletonMiddleware);
  search.use(chamfrColumnConfigMiddleware({ formatNumbers: true }));

  const defaultSortBy = categorySetup.sorting?.orderby;
  const defaultSortOrder = categorySetup.sorting?.order;

  const defaultIndex = defaultSortBy
    ? `${algolia.product_index_name}_${defaultSortBy}_${defaultSortOrder}`
    : algolia.product_index_name;

  const products = index({
    indexName: defaultIndex,
    indexId: "products",
  });

  const widgets = [
    configure({
      getRankingInfo: true,
      clickAnalytics: true,
      filters: buildFilter({
        supplierName,
        categoryName: category?.name,
        subcategoryName: getSelectedSubcategory()?.name,
      }),
      index: defaultIndex,
    }),
    chamfrSearchBox({
      container: supplierStoreElement.querySelector(".search-form"),
      label: "Search within these results",
    }),
    chamfrResultsHeader({
      container: supplierStoreElement.querySelector(".results-header"),
      scopeLabel: () => getSelectedSubcategory()?.name || category?.name,
    }),
    hitsPerPage({
      container: supplierStoreElement.querySelector(".display-per-page span"),
      items: [
        { label: "50 Items", value: 50, default: true },
        { label: "100 Items", value: 100 },
        { label: "250 Items", value: 250 },
      ],
    }),
    chamfrPagination({
      container: supplierStoreElement.querySelector(".chamfr-pagination"),
    }),
    chamfrClearAllFilters({
      container: supplierStoreElement.querySelector(
        ".selected-filters .clear-all-filters"
      ),
    }),
  ];

  const filtersContainer = supplierStoreElement.querySelector(
    ".search-results-leftside"
  );

  const customLabels = createCustomLabels(filters);
  const tableColumns = createTableColumns(columns, { formatNumbers: true });

  widgets.push(
    ...renderFilters(filters, filtersContainer, supplierStoreElement)
  );

  widgets.push(
    chamfrSelectedFilters({
      container: supplierStoreElement.querySelector(".selected-filters ul"),
      customLabels: Object.fromEntries(customLabels),
    })
  );

  widgets.push(
    chamfrProductsSortBy({
      container: supplierStoreElement.querySelector("table.products .header"),
      items: [],
      defaultIndex: defaultIndex,
      columns: tableColumns,
    })
  );

  widgets.push(
    chamfrProductsHits({
      container: supplierStoreElement.querySelector("table.products .list"),
      columns: tableColumns,
    })
  );

  products.addWidgets(widgets);

  search.addWidgets([products]);

  search.start();

  return search;
};

export const initializeSupplierStore = async (supplierStoreElement) => {
  const { supplierName } = supplierStoreElement.dataset;

  if (!supplierName) {
    return;
  }

  const client = algoliasearch(algolia.application_id, algolia.search_api_key);
  const searchClient = createSupplierStoreSearchClient(client);
  const browser = createCategoryBrowser(supplierStoreElement);
  let categories = [];
  let subcategories = [];
  let activeCategory = null;
  let selectedSubcategory = null;
  let search = null;
  let activationRequestId = 0;

  const getSelectedSubcategory = () => selectedSubcategory;

  const syncActiveCategoryDataset = () => {
    if (activeCategory?.slug) {
      supplierStoreElement.dataset.activeCategorySlug = activeCategory.slug;
    } else {
      delete supplierStoreElement.dataset.activeCategorySlug;
    }
  };

  const disposeSearch = () => {
    if (search) {
      search.dispose();
      search = null;
    }

    clearSearchContainers(supplierStoreElement);
  };

  const syncBrowserAndSearch = ({ resetProductsRouteState = false } = {}) => {
    syncActiveCategoryDataset();

    renderCategoryBrowser({
      browser,
      categories,
      activeCategory,
      subcategories,
      selectedSubcategory,
    });

    setHashState({
      category: activeCategory?.slug,
      subcategory: selectedSubcategory?.slug,
      resetProductsRouteState,
    });

    search = mountCategorySearch({
      supplierStoreElement,
      supplierName,
      category: activeCategory,
      getSelectedSubcategory,
      searchClient,
    });
  };

  const mountSellerFallbackSearch = ({ resetProductsRouteState = false } = {}) => {
    activeCategory = null;
    selectedSubcategory = null;
    subcategories = [];

    syncBrowserAndSearch({ resetProductsRouteState });
  };

  const activateCategory = async (
    category,
    nextSubcategorySlug = null,
    { resetProductsRouteState = false } = {},
  ) => {
    if (!category) {
      return;
    }

    const requestId = ++activationRequestId;

    disposeSearch();

    activeCategory = category;
    selectedSubcategory = null;
    subcategories = [];
    syncActiveCategoryDataset();
    renderCategoryBrowser({
      browser,
      categories,
      activeCategory,
      subcategories,
      selectedSubcategory,
    });
    setHashState({
      category: activeCategory.slug,
      subcategory: null,
      resetProductsRouteState,
    });

    subcategories = await fetchSubcategories({
      client,
      supplierName,
      category,
    });

    if (requestId !== activationRequestId) {
      return;
    }

    if (nextSubcategorySlug) {
      selectedSubcategory =
        subcategories.find((subcategory) => subcategory.slug === nextSubcategorySlug) ||
        null;
    }

    syncBrowserAndSearch({ resetProductsRouteState });
  };

  const toggleSubcategory = (subcategory) => {
    if (!activeCategory) {
      return;
    }

    disposeSearch();

    selectedSubcategory =
      selectedSubcategory?.slug === subcategory.slug ? null : subcategory;

    syncBrowserAndSearch({ resetProductsRouteState: true });
  };

  browser.addEventListener("click", (event) => {
    const target =
      event.target instanceof Element ? event.target : event.target?.parentElement;

    if (!target) {
      return;
    }

    const categoryButton = target.closest("[data-supplier-category]");
    const subcategoryButton = target.closest("[data-supplier-subcategory]");

    if (categoryButton) {
      const nextCategory = findCategoryBySlug(
        categories,
        categoryButton.dataset.supplierCategory,
      );

      if (nextCategory && nextCategory.slug !== activeCategory?.slug) {
        activateCategory(nextCategory, null, { resetProductsRouteState: true });
      }

      return;
    }

    if (subcategoryButton) {
      const nextSubcategory = findCategoryBySlug(
        subcategories,
        subcategoryButton.dataset.supplierSubcategory,
      );

      if (nextSubcategory) {
        toggleSubcategory(nextSubcategory);
      }
    }
  });

  categories = await fetchCategoryCounts({ client, supplierName });

  if (!categories.length) {
    mountSellerFallbackSearch();
    return;
  }

  const hashState = getHashState();
  const defaultCategory = findCategoryBySlug(categories, hashState.category) || categories[0];

  await activateCategory(defaultCategory, hashState.subcategory);
};
